import { observer } from 'mobx-react-lite'
import Link from 'next/link'
import {
  EditOutlined,
  RightOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CloseOutlined,
} from '@ant-design/icons'
import { useStores } from '~/hooks/useStores'
import { MyBtn } from '../ui/MyBtn'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'

import styles from './UserModal.module.css'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Cookies from 'js-cookie'
import { useAuth } from '~/hooks/useAuth'
import { useMutation } from '@apollo/client'
import { LOGOUT, MALL_CART_LIST } from '~/graphqls/gqls'
import { logout, mallCartList, mallCartListVariables } from '~/graphqls/types'
import { useRouter } from 'next/router'
import { useReload } from '~/hooks/useReload'
import { FenToYuan, digitalUnit } from '~/utils'
import { MyIcon } from '../ui/MyIcon'
import { AiEquity } from '~/graphqls/types'
import { CDNImage } from '../ui/CDNImage'
type Menu_type = {
  icon: string
  label: string
  href?: string
  /** 外部链接 */
  outerLink?: boolean
  /** 展示数量 */
  showNum?: boolean
  onClick?: () => void
}
interface Props {
  // visible: boolean
  close: () => void
  visible: boolean
}

const WUJIEAI_HOST = process.env.NEXT_PUBLIC_WUJIEAI_HOST

export const UserModal = observer(({ close, visible }: Props) => {
  // 购物车数量
  const [cartNum, setCartNum] = useState(0)
  const { store } = useStores()
  const router = useRouter()
  const { reload } = useReload()
  const { isCreator, isAiCreator } = useAuth()
  const cartNumAction = useImperativeQuery<mallCartList, mallCartListVariables>(
    MALL_CART_LIST
  )
  const [logoutAction] = useMutation<logout>(LOGOUT)
  const setShowAmount = useCallback(
    (status: boolean) => {
      Cookies.set('show-amount', String(status), {
        expires: 365,
      })
      store.setShowAmount(status)
    },
    [store.setShowAmount]
  )
  //最高等级会员
  const aiEquity = useMemo(() => {
    return store.aiEquity?.[store.aiEquity?.length - 1] || null
  }, [store.aiEquity])

  const onCreateCenter = () => {
    if (isCreator || isAiCreator) {
      router.push(`/creator-center`)
    } else {
      router.push('/to-be-creator')
    }
  }
  const onLogout = () => {
    logoutAction()
    Cookies.remove('gate-token')
    close()
    reload()
  }

  let optionalMenu: Menu_type[] = [
    {
      icon: 'integral',
      label: '我的积分',
      href: `/integral`,
    },
    {
      icon: 'heart',
      label: '赞和收藏',
      href: `/my/like`,
    },
    {
      icon: 'ai',
      label: 'AI创作',
      href: WUJIEAI_HOST + '/ai',
      outerLink: true,
    },
    {
      icon: 'sharesquare',
      label: '邀请好友',
      href: '/my/invite',
    },
    {
      icon: 'shoppingcart',
      label: '购物车',
      href: '/mall/cart',
      showNum: true,
    },
    {
      icon: 'receipt',
      label: '文创订单',
      href: '/mall/order/list',
    },
    {
      icon: 'certificate',
      label: '我的存证',
      href: `/my/certificate`,
    },
    {
      icon: 'shieldcheck',
      label: '安全认证',
      href: '/settings/safety',
    },
    {
      icon: 'user-1',
      label: '设置',
      href: '/settings/password',
    },
    {
      icon: 'signout',
      label: '退出登录',
      onClick: onLogout,
    },
  ]

  if (!(isCreator || isAiCreator)) {
    optionalMenu = optionalMenu.filter(item => {
      return !['作品集'].includes(item.label)
    })
  }

  const level = useMemo(() => {
    return AiEquity?.filter(it => it.type === aiEquity?.cardType)?.[0]?.level
  }, [aiEquity])

  useEffect(() => {
    if (visible) {
      cartNumAction({
        isCart: false,
      }).then(res => {
        setCartNum(res?.data?.itemModule?.listItemCart?.length || 0)
      })
    }
  }, [visible])
  return (
    <div
      className={classNames(
        'flex flex-col w-[600px] bg-neutral-800 px-4 py-8 relative',
        styles.modal
      )}
    >
      <div className="absolute top-8 right-4">
        <MyBtn
          type="default"
          shape="circle"
          className="!w-10 !h-10"
          onClick={close}
        >
          <CloseOutlined />
        </MyBtn>
      </div>
      <div className="flex items-center">
        <span className="text-lg font-semibold text-neutral-200 mr-3 max-w-[300px] line-clamp-1">
          {store.userinfo?.nickname}
        </span>
        {aiEquity?.cardType ? (
          <Link
            href={WUJIEAI_HOST + `/ai/equity-card?type=${aiEquity?.cardType}`}
          >
            <a target="_blank">
              <div className={classNames(styles.equity, [`equity-l${level}`])}>
                {level === 0 ? (
                  <div className="flex items-center text-neutral-400">
                    <MyIcon icon="level-origin" className="text-[20px] mr-1" />
                    <span>{aiEquity?.name}</span>
                  </div>
                ) : (
                  <>
                    <span className={styles.equityIcon}>
                      <CDNImage
                        className="w-full h-full"
                        src={`/images/ai/equity/${aiEquity?.cardType}.svg`}
                      />
                    </span>
                    <span>{aiEquity?.name}</span>
                    <span>
                      L
                      {
                        AiEquity?.filter(
                          it => it.type === aiEquity?.cardType
                        )?.[0]?.level
                      }
                    </span>
                  </>
                )}
              </div>
            </a>
          </Link>
        ) : (
          <Link href={WUJIEAI_HOST + `/ai/equity-card`}>
            <a target="_blank">
              <MyBtn
                type="primary"
                size="small"
                className="!h-8 mr-3 !text-primary !bg-[#E4DCFB] !border-[#F4F5F6] !font-semibold"
              >
                开通 AI 创作权益卡
              </MyBtn>
            </a>
          </Link>
        )}

        <MyBtn type="default" shape="circle">
          <Link href="/settings/base">
            <a>
              <EditOutlined />
            </a>
          </Link>
        </MyBtn>
      </div>

      <div className="flex items-center text-xs mt-2">
        <Link href={`/user/${store.userinfo?.base?.uid}/following`}>
          <a>
            <span className="text-neutral-400">关注 </span>
            <span className="text-neutral-200">
              {digitalUnit(store.userinfo?.social?.followCount)}
            </span>
          </a>
        </Link>
        <Link href={`/user/${store.userinfo?.base?.uid}/follower`}>
          <a className="ml-6">
            <span className="text-neutral-400">粉丝 </span>
            <span className="text-neutral-200">
              {digitalUnit(store.userinfo?.social?.fansCount)}
            </span>
          </a>
        </Link>
        {isCreator || isAiCreator ? (
          <span className="ml-6">
            <span className="text-neutral-400">获赞 </span>
            <span className="text-neutral-200">
              {digitalUnit(store.userinfo?.social?.likesCount)}
            </span>
          </span>
        ) : null}
      </div>
      <Link href="/wallet">
        <a>
          <div
            className={classNames(
              'flex items-center justify-between my-6 rounded-lg text-neutral-600 p-3',
              styles.wallet
            )}
          >
            <div>
              <div>
                账户余额（元）
                <span
                  className="cursor-pointer"
                  onClick={e => {
                    e.preventDefault()
                    setShowAmount(!store.showAmount)
                  }}
                >
                  {store.showAmount ? (
                    <EyeOutlined />
                  ) : (
                    <EyeInvisibleOutlined />
                  )}
                </span>
              </div>
              <div className="text-lg text-neutral-800">
                {store.showAmount
                  ? '¥ ' +
                    FenToYuan(
                      store.userinfo?.metaWalletBalance?.[0]
                        ?.availableBalance || 0
                    )
                  : '*****'}
              </div>
            </div>
            <RightOutlined />
          </div>
        </a>
      </Link>

      <div>
        <div className="text-sm text-neutral-200 font-medium ml-4">
          常用功能
        </div>
        <div className="flex items-center justify-between px-6 pt-4">
          {[
            {
              img: '/images/user/homepage.svg',
              label: '我的主页',
              href: `/user/${store.userinfo?.base?.uid}`,
            },
            {
              img: '/images/user/creator-center.svg',
              label: '创作者中心',
              onClick: onCreateCenter,
            },
            {
              img: '/images/user/wallet.svg',
              label: '我的钱包',
              href: `/wallet`,
            },
            {
              img: '/images/user/trade-history.svg',
              label: '交易记录',
              href: `/my/trade-history`,
            },
          ].map(item => {
            return (
              <div
                key={item.label}
                className="text-center cursor-pointer"
                onClick={
                  item.onClick ||
                  (() => {
                    router.push(item.href)
                  })
                }
              >
                <div>
                  <img
                    className="transition-all hover:scale-110"
                    src={item.img}
                    alt={item.label}
                  ></img>
                </div>

                <div className="pt-2 text-neutral-200 text-xs">
                  {item.label}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="mt-6">
        <div className="text-sm text-neutral-200 font-medium ml-4">
          其它功能
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          {optionalMenu.map(item => {
            return (
              <div
                key={item.label}
                className="text-center cursor-pointer w-[133px] h-9 px-7 flex items-center mt-1 rounded-lg hover:bg-neutral-700 transition-all"
                onClick={
                  item.onClick ||
                  (() => {
                    if (item.href) {
                      if (item.outerLink) {
                        window.open(item.href)
                      } else {
                        router.push(item.href)
                      }
                    }
                  })
                }
              >
                <span className="relative">
                  <MyIcon icon={item.icon} className="text-[24px]" />
                  {item?.showNum && cartNum ? (
                    <span className="absolute -top-[2px] -right-1  px-[5px] h-[15px] rounded-[180px] leading-[15px] text-center bg-error text-xs text-neutral-800 font-medium scale-[0.8]">
                      {cartNum}
                    </span>
                  ) : null}
                </span>
                <div className="ml-2 text-neutral-200 text-xs whitespace-nowrap">
                  {item.label}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
})
